import React, { useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client';
import { Translate } from 'react-localize-redux';
import classNames from 'classnames';
import css from './UserConfigurationShelf.css';
import { Shelf, Button, Spinner, TextField, Table, TableCell, WarningMessage } from '../../../components/widgets';
import TableCellButton from '../../../components/widgets/TableCellButton';
import { GET_PARTNER_ACCOUNTS } from '../../queries';
import { partnerShape } from '../types';
import useOAuthFlow from '../../hooks/useOAuthFlow';
import { getTableConfig, getColumnValue } from './tableConfig';
import AddAccountIcon from '../../../resources/svg/partner_config/add_account.svg';

export const USER_CONFIGURATION_SHELF_DATA_TEST_ID = 'user-configuration-shelf';
export const ACCOUNTS_TABLE_DATA_TEST_ID = 'user-configuration-accounts-table';

const REAUTHORIZE_FIELD = 'reauthorize';

// Create a TableCell wrapper that uses getValue for columns with custom getters
const createCellWithGetter = getValue => props => <TableCell {...props} value={getValue(props.row)} />;

export default function UserConfigurationShelf({ open = false, partner, onClose = () => {}, onDone = null }) {
    const { id: partnerId, name: partnerName, displayName: partnerDisplayName, logoPath: icon } = partner;

    const [searchText, setSearchText] = useState('');
    const [sortColumn, setSortColumn] = useState(null);
    const [sortDirection, setSortDirection] = useState(null);

    const { startOAuthFlow, error: oauthError, clearError } = useOAuthFlow(partnerId, partnerName, partnerDisplayName);

    const partnerAccountsResponse = useQuery(GET_PARTNER_ACCOUNTS, {
        variables: { partnerId },
        skip: !open,
    });

    const accounts = partnerAccountsResponse.data?.partnerAccounts || [];

    // Get partner-specific table configuration
    const tableConfig = useMemo(() => getTableConfig(partnerName), [partnerName]);

    const filteredAccounts = useMemo(() => {
        if (!searchText) return accounts;
        const lowerSearch = searchText.toLowerCase();
        // Search across all columns
        return accounts.filter(account =>
            tableConfig.columns.some(col =>
                getColumnValue(account, col)
                    ?.toLowerCase()
                    .includes(lowerSearch)
            )
        );
    }, [accounts, searchText, tableConfig.columns]);

    const sortedAccounts = useMemo(() => {
        if (sortDirection === null || sortColumn === null) return filteredAccounts;

        const sortCol = tableConfig.columns.find(col => col.name === sortColumn);
        if (!sortCol) return filteredAccounts;

        return [...filteredAccounts].sort((a, b) => {
            const aVal = String(getColumnValue(a, sortCol) || '').toLowerCase();
            const bVal = String(getColumnValue(b, sortCol) || '').toLowerCase();
            if (sortDirection === 'asc') return aVal.localeCompare(bVal);
            return bVal.localeCompare(aVal);
        });
    }, [filteredAccounts, sortDirection, sortColumn, tableConfig.columns]);

    const triggerOAuthFlow = useCallback(() => {
        clearError();
        startOAuthFlow();
    }, [clearError, startOAuthFlow]);

    const handleDone = () => {
        (onDone || onClose)();
    };

    // Build dynamic table metadata based on partner configuration
    const tableMetadata = useMemo(() => {
        const handleColumnSort = columnName => {
            if (sortColumn === columnName) {
                setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
            } else {
                setSortColumn(columnName);
                setSortDirection('asc');
            }
        };

        const columnMetadata = tableConfig.columns.map(col => ({
            name: col.name,
            displayName: col.displayName,
            cellComponent: col.getValue ? createCellWithGetter(col.getValue) : TableCell,
            headerAlign: 'left',
            headerWidth: col.headerWidth,
            boldHeader: true,
            showHeaderArrow: true,
            headerArrow: sortColumn === col.name ? (sortDirection === 'asc' ? 'up' : 'down') : null,
            headerClick: () => handleColumnSort(col.name),
        }));

        // Add reauthorize button column
        columnMetadata.push({
            name: REAUTHORIZE_FIELD,
            displayName: '',
            cellComponent: TableCellButton,
            headerAlign: 'right',
            cellProps: {
                translationId: 'STATIC.PAGES.PARTNER_CONFIGURATION.USER_CONFIGURATION_SHELF.REAUTHORIZE',
                type: 'secondary',
                onClick: triggerOAuthFlow,
                tdClassName: css.reauthorizeCell,
            },
        });

        return columnMetadata;
    }, [tableConfig, sortColumn, sortDirection, triggerOAuthFlow]);

    // Disable Done button when account setup is required (onDone provided) but no accounts exist
    const isDoneDisabled = onDone && accounts.length === 0;

    const footerButtons = [
        {
            type: 'primaryButton',
            text: 'STATIC.BUTTONS.DONE',
            onClick: handleDone,
            disabled: isDoneDisabled,
        },
    ];

    const getShelfContent = () => {
        if (partnerAccountsResponse.loading) {
            return <Spinner show expanded />;
        }

        if (partnerAccountsResponse.error) {
            return (
                <WarningMessage
                    show
                    type="error"
                    message="STATIC.PAGES.PARTNER_CONFIGURATION.USER_CONFIGURATION_SHELF.LOAD_ERROR"
                    showIcon
                />
            );
        }

        return (
            <div className={css.userConfigurationShelf}>
                <div className={css.headerSection}>
                    <div className={css.headerInfo}>
                        <h3 className={css.title}>
                            <Translate
                                id="STATIC.PAGES.PARTNER_CONFIGURATION.USER_CONFIGURATION_SHELF.TITLE"
                                data={{ partnerName: partnerDisplayName }}
                            />
                        </h3>
                        <p className={css.description}>
                            <Translate
                                id="STATIC.PAGES.PARTNER_CONFIGURATION.USER_CONFIGURATION_SHELF.DESCRIPTION"
                                data={{ partnerName: partnerDisplayName }}
                            />
                        </p>
                    </div>
                    <Button type="primary" onClick={triggerOAuthFlow} className={css.addUserButton}>
                        <Translate id="STATIC.PAGES.PARTNER_CONFIGURATION.USER_CONFIGURATION_SHELF.ADD_USER" />
                    </Button>
                </div>

                {oauthError && <WarningMessage show type="error" message={oauthError} showIcon />}

                <div className={css.contentWrapper}>
                    <div className={css.searchSection}>
                        <TextField
                            placeholder="STATIC.PAGES.PARTNER_CONFIGURATION.USER_CONFIGURATION_SHELF.SEARCH_PLACEHOLDER"
                            value={searchText}
                            onChange={value => setSearchText(value)}
                            containerClass={css.searchInput}
                        />
                    </div>

                    <div className={css.accountsSection}>
                        <div className={css.accountsHeader}>
                            <img src={icon} alt={partnerDisplayName} className={css.partnerLogo} />
                            <span className={css.accountsTitle}>
                                <Translate
                                    id="STATIC.PAGES.PARTNER_CONFIGURATION.USER_CONFIGURATION_SHELF.ACCOUNTS_HEADER"
                                    data={{ partnerName: partnerDisplayName, count: accounts.length }}
                                />
                            </span>
                        </div>

                        {accounts.length === 0 ? (
                            <div className={classNames(css.emptyState, css.clickable)} onClick={triggerOAuthFlow}>
                                <AddAccountIcon className={css.emptyStateIcon} />
                                <Translate id="STATIC.PAGES.PARTNER_CONFIGURATION.USER_CONFIGURATION_SHELF.NO_ACCOUNTS" />
                            </div>
                        ) : filteredAccounts.length === 0 ? (
                            <div className={css.emptyState}>
                                <Translate id="STATIC.PAGES.PARTNER_CONFIGURATION.USER_CONFIGURATION_SHELF.NO_MATCHING_ACCOUNTS" />
                            </div>
                        ) : (
                            <Table
                                dataTestId={ACCOUNTS_TABLE_DATA_TEST_ID}
                                data={sortedAccounts}
                                metadata={tableMetadata}
                                trClass={css.tableRow}
                                theadClass={classNames(css.tableHeader, {
                                    [css.showHeaderSeparators]: tableConfig.showHeaderSeparators,
                                })}
                                zebraTable={false}
                                rowHoverEnabled={false}
                                showInnerBorder
                            />
                        )}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <Shelf
            open={open}
            shelfSize="large"
            headerText="STATIC.PAGES.PARTNER_CONFIGURATION.USER_CONFIGURATION"
            headerImagePath={icon}
            enterAnimationDisabled={false}
            onClose={onClose}
            dataTestId={USER_CONFIGURATION_SHELF_DATA_TEST_ID}
            footerNavigationButtons={footerButtons}
        >
            {getShelfContent()}
        </Shelf>
    );
}

UserConfigurationShelf.propTypes = {
    open: PropTypes.bool,
    partner: partnerShape.isRequired,
    onClose: PropTypes.func,
    onDone: PropTypes.func,
};
